// BE-aligned enums (mirror docs/frontend/007-ai-assistant-and-tickets-handoff.md)

export enum ESupportTicketStatus {
  Open = "open",
  InProgress = "in_progress",
  WaitingForCustomer = "waiting_for_customer",
  Resolved = "resolved",
  Closed = "closed",
}

export enum ESupportTicketPriority {
  Urgent = "urgent",
  Alert = "alert",
  FollowUp = "follow_up",
  Reminder = "reminder",
  Low = "low",
}

export enum ESupportTicketSource {
  WhatsApp = "whatsapp",
  InAppSupport = "in_app_support",
}

export enum ESupportTicketKind {
  General = "general",
  ReturnRequest = "return_request",
}

export interface SupportTicketResponse {
  id: string;
  title: string;
  aiSummary: string | null;
  kind: ESupportTicketKind;
  orderId: string | null;
  complainedAt: string | null;
  priority: ESupportTicketPriority;
  status: ESupportTicketStatus;
  source: ESupportTicketSource;
  whatsappConversationId: string | null;
  inAppConversationId: string | null;
  resellerId: string | null;
  assignedAdminId: string | null;
  createdByAi: boolean;
  createdAt: string;
  updatedAt: string;
}

// UI shape (existing widget contract)

export type TicketStatus = "Open" | "Processing" | "Resolved" | "Closed";

export interface AITicket {
  id: string;
  title: string;
  description: string;
  assignedAgent: {
    name: string;
    letter?: string;
    color?: string;
  } | null;
  status: TicketStatus;
  dateCreated: string;
  customer?: {
    name: string;
    phone: string;
  };
  body?: string;
  // BE-aligned passthroughs needed for actions / WS-driven cache updates
  raw: SupportTicketResponse;
}

// Return flow (kind === ReturnRequest)

export enum EWhatsAppMediaType {
  Image = "image",
  Video = "video",
}

export interface TicketReturnMedia {
  whatsappMessageId: string;
  messageType: EWhatsAppMediaType;
  caption: string | null;
  url: string | null;
  receivedAt: string;
  // Picked by the assistant when it opened the ticket — pre-checked in the form.
  isEvidence: boolean;
  // Already evidence on a different return. Marked, not blocked.
  usedOnAnotherReturn: boolean;
}

export interface TicketReturnItem {
  barcodeId: string;
  name: string;
  quantity: number;
  returnQuantity: number;
  imageUrl: string | null;
  packGroupId: string | null;
}

export interface TicketReturnContext {
  // Null for a manual return, which has no ticket.
  ticketId: string | null;
  orderId: string;
  orderStatus: string;
  // Nothing left to return — the ticket shows a badge instead of the button.
  allItemsReturned: boolean;
  // False once the order has left Delivered, which the server refuses anyway.
  canCreateReturn: boolean;
  // A return already exists on this order — including one that covers only part
  // of it. The rest cannot be added to it; see the badge in TicketModal.
  hasReturn: boolean;
  complainedAt: string;
  returnDeadlineAt: string | null;
  isWithinDeadline: boolean;
  deliveryAddress: {
    id: string;
    fullName: string;
    phoneNumber: string;
    address: string;
  } | null;
  items: TicketReturnItem[];
  media: TicketReturnMedia[];
}

// Manual return — an agent starting one from the conversation, when the
// assistant never recognised the complaint as a return.

export interface ManualReturnOrder {
  orderId: string;
  deliveredAt: string | null;
  orderStatus: string;
  itemCount: number;
  totalPrice: number;
  isWithinDeadline: boolean;
  // A return already exists on this order — shown so an agent opens it instead
  // of raising a second one.
  hasReturn: boolean;
  existingTicket: {
    id: string;
    createdByAi: boolean;
    status: ESupportTicketStatus;
  } | null;
}

export interface ManualReturnContext {
  // False = the AI saw no return here. That is what the confirmation warns about.
  hasReturnTicket: boolean;
  orders: ManualReturnOrder[];
}
